import React, { useEffect, useMemo, useState } from 'react'
import { UPDATES, type UpdateEntry } from './updatesData'
import { GENERATED_UPDATES } from './generatedUpdates'

/**
 * Full feed = auto-generated entries (one per commit since the curated
 * boundary — regenerated on EVERY build/deploy by scripts/generate-updates.cjs,
 * so the page keeps itself current) + the hand-curated history. Newest first.
 */
const ALL_UPDATES: UpdateEntry[] = [...GENERATED_UPDATES, ...UPDATES].sort(
  (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)
)

/**
 * Platform Updates — release notes for the Support + CRM platform, in the same
 * format as the WeQuote product updates page: a quarter filter top-right, and
 * per-entry rows with date/title/tags on the left and the details (plus an
 * optional screenshot) on the right.
 *
 * Content lives in updatesData.ts. Screenshots: drop a PNG under
 * public/updates/ and set `image` on the entry.
 */

const TAG_STYLES: Record<UpdateEntry['tags'][number], string> = {
  'Platform-wide': 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900',
  CRM: 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
  Support: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
  Widget: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
  Mobile: 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
  AI: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
}

/** 'Q3 2026' style quarter key for an ISO date. */
function quarterOf(iso: string): string {
  const d = new Date(iso)
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export const PlatformUpdatesPage: React.FC = () => {
  // Quarters present in the data, newest first. 'All' shows everything.
  const quarters = useMemo(() => {
    const qs: string[] = []
    for (const u of ALL_UPDATES) {
      const q = quarterOf(u.date)
      if (!qs.includes(q)) qs.push(q)
    }
    return qs
  }, [])

  const [quarter, setQuarter] = useState<string>('All')

  const visible = useMemo(
    () => (quarter === 'All' ? ALL_UPDATES : ALL_UPDATES.filter((u) => quarterOf(u.date) === quarter)),
    [quarter]
  )

  // Pagination — 50 entries per page.
  const PAGE_SIZE = 50
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  // Reset to the first page whenever the filter (and thus the list) changes.
  useEffect(() => { setPage(1) }, [quarter])
  // Keep the page in range if the list shrinks.
  useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])
  const paged = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  )
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }) }, [page])

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-8 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-txt-primary">Platform Updates and Release Notes</h1>
            <p className="text-base text-txt-secondary mt-3">
              The latest updates to the Support platform and CRM — everything we ship, as we ship it.
            </p>
          </div>
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
            className="shrink-0 px-3.5 py-2 rounded-lg border border-border bg-surface text-sm font-semibold text-txt-primary outline-none focus:ring-2 focus:ring-brand-500/40 cursor-pointer"
          >
            <option value="All">All updates</option>
            {quarters.map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
        </div>

        {/* Entries */}
        <div className="mt-10 divide-y divide-border">
          {paged.map((u, i) => (
            <article key={`${u.date}-${u.title}-${i}`} className="py-10 first:pt-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 md:gap-10">
              {/* Left: date, title, tags */}
              <div>
                <p className="text-sm text-txt-tertiary">{formatDate(u.date)}</p>
                <h2 className="text-lg font-bold text-txt-primary mt-2 leading-snug">{u.title}</h2>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {u.tags.map((t) => (
                    <span key={t} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${TAG_STYLES[t]}`}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right: bullets + optional screenshot */}
              <div className="min-w-0">
                <ul className="space-y-2.5">
                  {u.bullets.map((b, j) => (
                    <li key={j} className="flex gap-2.5 text-[15px] leading-relaxed text-txt-primary">
                      <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-txt-tertiary shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {u.image && (
                  <div className="mt-6 rounded-xl border border-border overflow-hidden shadow-sm bg-surface">
                    <img src={u.image} alt={`${u.title} screenshot`} className="w-full h-auto block" loading="lazy" />
                  </div>
                )}
              </div>
            </article>
          ))}
          {visible.length === 0 && (
            <p className="py-16 text-center text-sm text-txt-tertiary">No updates in this period.</p>
          )}
        </div>

        {/* Pagination — 50 per page */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
            <p className="text-sm text-txt-tertiary">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, visible.length)} of {visible.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3.5 py-2 rounded-lg border border-border bg-surface text-sm font-medium text-txt-primary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-txt-secondary tabular-nums px-1">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3.5 py-2 rounded-lg border border-border bg-surface text-sm font-medium text-txt-primary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
